import {
  CalendarDays,
  Check,
  ChevronRight,
  FileText,
  History,
  LogOut,
  Mic,
  NotebookPen,
  Pause,
  ShieldCheck,
  Sparkles,
  Unplug,
  Volume2
} from "lucide-react";
import type {
  GoogleConnectionStatus,
  PermissionMode,
  Preferences,
  ProviderHealth
} from "../types";

const destinations: Array<{
  id: "note" | "google_docs" | "calendar";
  label: string;
  description: string;
  icon: typeof NotebookPen;
}> = [
  {
    id: "note",
    label: "Notes ParleNote",
    description: "Enregistrées dans votre espace privé",
    icon: NotebookPen
  },
  {
    id: "google_docs",
    label: "Google Docs",
    description: "Documents créés par ParleNote uniquement",
    icon: FileText
  },
  {
    id: "calendar",
    label: "Google Agenda",
    description: "Événements sur vos agendas autorisés",
    icon: CalendarDays
  }
];

const modeOptions: Array<{ value: PermissionMode; label: string }> = [
  { value: "always_ask", label: "Toujours demander" },
  { value: "guarded_auto", label: "Auto. avec garde-fous" },
  { value: "never", label: "Désactivé" }
];

export function SettingsView({
  preferences,
  health,
  google,
  userEmail,
  voiceName,
  onPreferences,
  onRequestMicrophone,
  onReconnectGoogle,
  onSignOut,
  onDisconnectGoogle,
  onClearHistory
}: {
  preferences: Preferences;
  health: ProviderHealth;
  google: GoogleConnectionStatus;
  userEmail?: string;
  voiceName?: string;
  onPreferences: (preferences: Preferences) => void;
  onRequestMicrophone: () => Promise<void>;
  onReconnectGoogle: () => Promise<void>;
  onSignOut: () => Promise<void>;
  onDisconnectGoogle: () => Promise<void>;
  onClearHistory: () => Promise<void>;
}) {
  const updateMode = (
    destination: "note" | "google_docs" | "calendar",
    mode: PermissionMode
  ) => onPreferences({ ...preferences, [destination]: mode });

  const updateService = (service: "googleDocsEnabled" | "calendarEnabled", value: boolean) =>
    onPreferences({ ...preferences, [service]: value });

  return (
    <section className="settings-view" aria-labelledby="settings-title">
      <header className="settings-header">
        <p className="eyebrow">Votre espace</p>
        <h1 id="settings-title">Réglages</h1>
        <p>Tout ce que ParleNote peut écouter, mémoriser et créer reste sous votre contrôle.</p>
      </header>

      <section className="settings-group" aria-labelledby="account-title">
        <h2 id="account-title">Compte Google</h2>
        <div className="account-card">
          <div className="account-avatar" aria-hidden="true">
            {(userEmail || "P").slice(0, 1).toUpperCase()}
          </div>
          <div className="account-copy">
            <strong>{userEmail}</strong>
            <span>
              <Check aria-hidden="true" /> Connecté · notes synchronisées
            </span>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={() => void onSignOut()}
            aria-label="Se déconnecter"
          >
            <LogOut aria-hidden="true" />
          </button>
        </div>
      </section>

      <section className="settings-group" aria-labelledby="services-title">
        <div className="group-title">
          <div>
            <h2 id="services-title">Services Google</h2>
            <p>Autorisés avec votre connexion initiale, désactivables à tout moment.</p>
          </div>
          <span className={`status-pill ${google.connected ? "online" : "warning"}`}>
            {google.connected ? "Connecté" : "À finaliser"}
          </span>
        </div>

        <div className="settings-list">
          <label className="setting-row">
            <span className="setting-icon blue"><FileText aria-hidden="true" /></span>
            <span className="setting-copy">
              <strong>Google Docs</strong>
              <small>
                {google.docs ? "Accès prêt" : "Autorisation Docs manquante"}
              </small>
            </span>
            <input
              className="switch-input"
              type="checkbox"
              checked={preferences.googleDocsEnabled}
              onChange={(event) => updateService("googleDocsEnabled", event.target.checked)}
              aria-label="Activer Google Docs"
            />
          </label>
          <label className="setting-row">
            <span className="setting-icon coral"><CalendarDays aria-hidden="true" /></span>
            <span className="setting-copy">
              <strong>Google Agenda</strong>
              <small>
                {google.calendar ? "Accès prêt" : "Autorisation Agenda manquante"}
              </small>
            </span>
            <input
              className="switch-input"
              type="checkbox"
              checked={preferences.calendarEnabled}
              onChange={(event) => updateService("calendarEnabled", event.target.checked)}
              aria-label="Activer Google Agenda"
            />
          </label>
        </div>
        {google.connected && (
          <button
            className="setting-row as-button danger-text"
            type="button"
            onClick={() => {
              if (
                window.confirm(
                  "Retirer l’accès de ParleNote à votre compte Google ?\n\nL’autorisation sera révoquée chez Google. Vos documents et événements déjà créés ne sont pas supprimés."
                )
              ) {
                void onDisconnectGoogle();
              }
            }}
          >
            <span className="setting-icon neutral"><Unplug aria-hidden="true" /></span>
            <span className="setting-copy">
              <strong>Retirer l’accès Google</strong>
              <small>Révoque l’autorisation et efface le jeton conservé</small>
            </span>
            <ChevronRight aria-hidden="true" />
          </button>
        )}

        {!google.connected && (
          <div className="settings-notice">
            <ShieldCheck aria-hidden="true" />
            <span>
              {google.reconnectReason === "configuration"
                ? "La configuration serveur Google doit être corrigée. Inutile de reconnecter votre compte."
                : google.reconnectReason === "provider"
                  ? "Google n’a pas pu renouveler l’accès pour le moment. Réessayez un peu plus tard."
                  : google.needsReconnect
                ? "L’autorisation Google doit être renouvelée pour continuer à créer des documents et événements."
                : "ParleNote demandera uniquement les accès Docs et Agenda minimaux."}
            </span>
            {google.reconnectReason !== "configuration" &&
              google.reconnectReason !== "provider" && (
                <button
                  className="button compact primary"
                  type="button"
                  onClick={() => void onReconnectGoogle()}
                >
                  Renouveler les accès Google
                </button>
              )}
          </div>
        )}
      </section>

      <section className="settings-group" aria-labelledby="assistant-title">
        <h2 id="assistant-title">Voix et mémoire</h2>
        <div className="settings-list">
          <button className="setting-row as-button" type="button" onClick={() => void onRequestMicrophone()}>
            <span className="setting-icon mint"><Mic aria-hidden="true" /></span>
            <span className="setting-copy">
              <strong>Microphone</strong>
              <small>Vérifier ou redemander l’autorisation</small>
            </span>
            <ChevronRight aria-hidden="true" />
          </button>
          <label className="setting-row">
            <span className="setting-icon violet"><Volume2 aria-hidden="true" /></span>
            <span className="setting-copy">
              <strong>Réponses vocales</strong>
              <small>{voiceName || "Voix française de l’appareil"}</small>
            </span>
            <input
              className="switch-input"
              type="checkbox"
              checked={preferences.autoSpeak}
              onChange={(event) =>
                onPreferences({ ...preferences, autoSpeak: event.target.checked })
              }
              aria-label="Lire les réponses à voix haute"
            />
          </label>
          <label className="setting-row">
            <span className="setting-icon mint"><Pause aria-hidden="true" /></span>
            <span className="setting-copy">
              <strong>Arrêt automatique</strong>
              <small>Envoyer seul après une pause, sans toucher le bouton</small>
            </span>
            <input
              className="switch-input"
              type="checkbox"
              checked={preferences.autoStopOnSilence}
              onChange={(event) =>
                onPreferences({ ...preferences, autoStopOnSilence: event.target.checked })
              }
              aria-label="Arrêter l’enregistrement après un silence"
            />
          </label>
          <label className="setting-row">
            <span className="setting-icon amber"><History aria-hidden="true" /></span>
            <span className="setting-copy">
              <strong>Mémoire de conversation</strong>
              <small>Contexte chiffré par votre session et isolé par compte</small>
            </span>
            <select
              value={preferences.historyDays}
              onChange={(event) =>
                onPreferences({
                  ...preferences,
                  historyDays: Number(event.target.value) as 7 | 30 | 90
                })
              }
              aria-label="Durée de l’historique"
            >
              <option value={7}>7 jours</option>
              <option value={30}>30 jours</option>
              <option value={90}>90 jours</option>
            </select>
          </label>
          <button className="setting-row as-button danger-text" type="button" onClick={() => void onClearHistory()}>
            <span className="setting-icon neutral"><History aria-hidden="true" /></span>
            <span className="setting-copy">
              <strong>Effacer la conversation</strong>
              <small>Les notes déjà créées ne seront pas supprimées</small>
            </span>
            <ChevronRight aria-hidden="true" />
          </button>
        </div>
      </section>

      <section className="settings-group permissions" aria-labelledby="permissions-title">
        <div className="group-title">
          <div>
            <h2 id="permissions-title">Écriture par destination</h2>
            <p>Choisissez quand une confirmation est nécessaire.</p>
          </div>
          <Sparkles aria-hidden="true" />
        </div>
        <div className="permission-list">
          {destinations.map(({ id, label, description, icon: Icon }) => (
            <div className="permission-row" key={id}>
              <span className="setting-icon neutral"><Icon aria-hidden="true" /></span>
              <div className="permission-copy">
                <strong>{label}</strong>
                <small>{description}</small>
              </div>
              <select
                value={preferences[id]}
                onChange={(event) => updateMode(id, event.target.value as PermissionMode)}
                aria-label={`Mode d’écriture pour ${label}`}
              >
                {modeOptions.map((option) => (
                  <option value={option.value} key={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
        <p className="guardrail">
          Les contenus ambigus, modifications et suppressions restent toujours à confirmer,
          même en mode automatique.
        </p>
      </section>

      {health.turnstile === "misconfigured" && (
        <div className="settings-notice" role="alert">
          <ShieldCheck aria-hidden="true" />
          <span>
            Le contrôle anti-robot est configuré à moitié : par sécurité, le serveur refuse
            tout traitement tant qu’un administrateur ne l’a pas complété.
          </span>
        </div>
      )}

      <footer className="settings-footnote">
        <ShieldCheck aria-hidden="true" />
        <span>
          Protection active : compte vérifié, données isolées, quotas serveur
          {health.turnstileSiteKey ? " et contrôle anti-abus." : "."}
        </span>
      </footer>
    </section>
  );
}
